// src/v2/playbooks/PlaybooksPage.jsx
//
// G-34-S2: Playbooks home — launcher-first design.
//
// Sections:
//   1. Header row: title, sub-copy, "+ New playbook" button (editor/admin)
//   2. "Start a playbook" — 4-col template tile grid
//   3. "Running now" — compact strip of active runs + "View all runs →" link

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { getPlaybookTemplates, getPlaybookRuns } from "../../data/playbooksClient.js";
import { PlaybookLaunchModal } from "./PlaybookLaunchModal.jsx";
import { log } from "../../utils/logger.js";

// ─── Icon map ─────────────────────────────────────────────────────────────────
// Maps template category/name to a display glyph. Falls back to "▶".
const ICON_MAP = {
  "closure":       "⇄",
  "relocation":    "⇄",
  "office closure":"⇄",
  "renewal":       "↻",
  "lease renewal": "↻",
  "consolidation": "⤢",
  "market consolidation": "⤢",
  "opening":       "＋",
  "new office":    "＋",
};

function templateIcon(t) {
  const name = (t.name || "").toLowerCase();
  for (const [key, icon] of Object.entries(ICON_MAP)) {
    if (name.includes(key)) return icon;
  }
  return "▶";
}

// ─── Phase-dots progress bar ──────────────────────────────────────────────────

function PhaseDots({ phaseCount, phasesDone, currentPhase }) {
  return (
    <div style={{ display: "flex", gap: 4, margin: "10px 0 9px" }}>
      {Array.from({ length: phaseCount }).map((_, i) => {
        const done = i < phasesDone;
        const cur  = i === (currentPhase ?? phasesDone);
        return (
          <div
            key={i}
            style={{
              flex: 1, height: 6, borderRadius: 3,
              background: done ? T.green : cur ? T.blue : T.border,
            }}
          />
        );
      })}
    </div>
  );
}

// ─── TemplateTile ─────────────────────────────────────────────────────────────

function TemplateTile({ template, onStart, canEdit }) {
  const isDraft = template.status === "draft";
  const icon    = templateIcon(template);

  return (
    <div
      data-testid={`template-tile-${template.id}`}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 13, padding: "16px 15px",
        cursor: canEdit ? "pointer" : "default",
        boxShadow: T.shadowCardXs,
        transition: "border-color .15s, box-shadow .15s",
        display: "flex", flexDirection: "column",
      }}
      onClick={() => canEdit && onStart(template.id)}
      onMouseEnter={(e) => {
        if (!canEdit) return;
        e.currentTarget.style.borderColor = T.blueSoftBorder;
        e.currentTarget.style.boxShadow = T.shadowHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.boxShadow = T.shadowCardXs;
      }}
    >
      {/* Icon */}
      <div style={{
        width: 42, height: 42, borderRadius: 11,
        background: isDraft ? T.lightGray : T.blueSoft,
        color: isDraft ? T.mid : T.blue,
        display: "grid", placeItems: "center",
        fontSize: T.fsLg5, marginBottom: 11,
      }}>
        {icon}
      </div>

      {/* Name + draft badge */}
      <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase5, lineHeight: 1.2 }}>
        {template.name}
        {isDraft && (
          <span style={{
            marginLeft: 7,
            fontSize: T.fsXxs, fontWeight: T.fwBold,
            background: T.ragAmberBg, color: T.ragAmberText,
            border: `1px solid ${T.ragAmberBorder}`,
            borderRadius: 5, padding: "1px 6px",
            verticalAlign: "middle",
          }}>
            Draft
          </span>
        )}
      </div>

      {/* Applies-to chip */}
      {template.appliesto && (
        <div style={{
          fontSize: T.fsXxs5, fontWeight: T.fwBold, color: T.light,
          textTransform: "uppercase", letterSpacing: "0.03em", marginTop: 3,
        }}>
          Applies to · {template.appliesto}
        </div>
      )}

      {/* Description */}
      {template.description && (
        <div style={{
          fontSize: T.fsXs5, color: T.mid, marginTop: 8, flex: 1,
          lineHeight: 1.5,
        }}>
          {template.description}
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "center",
        marginTop: 12, paddingTop: 10,
        borderTop: `1px solid ${T.border}`,
      }}>
        <span style={{ fontSize: T.fsXxs5, color: T.light }}>
          {template.checkCount} checks · {template.phaseCount} phases
        </span>
        {canEdit && (
          <span style={{
            marginLeft: "auto",
            fontSize: T.fsSm, fontWeight: T.fwBold,
            color: isDraft ? T.ragAmberText : T.blue,
          }}>
            {isDraft ? "Finish →" : "Start →"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── RunStrip — a compact active run row ─────────────────────────────────────

function RunStrip({ run, onOpen }) {
  const icon = ICON_MAP[
    Object.keys(ICON_MAP).find((k) => (run.name || "").toLowerCase().includes(k))
  ] ?? "▶";

  const phasesDone    = run.phasesDone    ?? 0;
  const phaseCount    = run.phaseCount    ?? 1;
  const currentPhase  = run.currentPhase  ?? phasesDone;

  return (
    <div
      data-testid={`run-strip-${run.id}`}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 13, padding: "14px 16px",
        boxShadow: T.shadowCardXs,
      }}
    >
      {/* Top row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 9,
          background: T.blueSoft, color: T.blue,
          display: "grid", placeItems: "center",
          fontSize: T.fsMd, flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase5 }}>{run.name}</div>
          {run.targetLabel && (
            <div style={{ fontSize: T.fsXs, color: T.light }}>{run.targetLabel}</div>
          )}
        </div>
        <div style={{ textAlign: "right", fontSize: T.fsXs, color: T.mid, flexShrink: 0 }}>
          <strong style={{ color: T.dark, fontSize: T.fsSm5 }}>
            Phase {Math.min(phasesDone + 1, phaseCount)} of {phaseCount}
          </strong>
          {run.owner && <div>owner {run.owner}</div>}
        </div>
      </div>

      {/* Phase dots */}
      <PhaseDots phaseCount={phaseCount} phasesDone={phasesDone} currentPhase={currentPhase} />

      {/* Next step + open link */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsSm }}>
        {run.nextCheck && (
          <>
            <span style={{
              fontSize: T.fsXxs, fontWeight: T.fwBold,
              textTransform: "uppercase", color: T.light,
            }}>
              Next
            </span>
            <strong style={{ color: T.dark, flex: 1, minWidth: 0 }}>{run.nextCheck}</strong>
          </>
        )}
        <button
          type="button"
          data-testid={`run-strip-open-${run.id}`}
          onClick={() => onOpen(run.id)}
          style={{
            marginLeft: "auto",
            background: "transparent", border: "none",
            fontSize: T.fsSm, fontWeight: T.fwBold,
            color: T.blue, cursor: "pointer", padding: 0,
          }}
        >
          Open →
        </button>
      </div>
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function RunSkeleton() {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderRadius: 13, padding: "14px 16px", height: 100,
      animation: "pulse 1.5s ease-in-out infinite",
    }} />
  );
}

// ─── PlaybooksPage ────────────────────────────────────────────────────────────

export function PlaybooksPage({ currentRole }) {
  const navigate   = useNavigate();
  const canEdit    = currentRole === "editor" || currentRole === "admin";

  const [templates,        setTemplates]        = useState([]);
  const [runs,             setRuns]             = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [runsLoading,      setRunsLoading]      = useState(true);
  const [launchTemplateId, setLaunchTemplateId] = useState(null);
  const [modalOpen,        setModalOpen]        = useState(false);

  // Load templates
  useEffect(() => {
    let cancelled = false;
    getPlaybookTemplates()
      .then((list) => { if (!cancelled) { setTemplates(Array.isArray(list) ? list : []); setTemplatesLoading(false); } })
      .catch((err) => { log.error("PlaybooksPage: failed to load templates", err); if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Load runs
  useEffect(() => {
    let cancelled = false;
    getPlaybookRuns()
      .then((list) => { if (!cancelled) { setRuns(Array.isArray(list) ? list : []); setRunsLoading(false); } })
      .catch((err) => { log.error("PlaybooksPage: failed to load runs", err); if (!cancelled) setRunsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const activeRuns = runs.filter((r) => r.status === "active" || r.status === "in_progress");

  function handleStartTile(templateId) {
    if (!canEdit) return;
    setLaunchTemplateId(templateId);
    setModalOpen(true);
  }

  function handleLaunched(runId) {
    setModalOpen(false);
    navigate(`/playbooks/runs/${encodeURIComponent(runId)}`);
  }

  return (
    <div
      data-testid="playbooks-page"
      style={{
        maxWidth: 1040,
        margin: "0 auto",
        padding: "20px 24px 70px",
        width: "100%",
      }}
    >
      {/* ── Header row ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 0 }}>
        <div>
          <h1 style={{ fontSize: T.fsLgX5, fontWeight: T.fwBold, color: T.dark, margin: 0 }}>
            Playbooks
          </h1>
          <div style={{ fontSize: T.fsSm5, color: T.mid, marginTop: 3 }}>
            Run the multi-step processes — and see what's in flight.
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            data-testid="new-playbook-btn"
            onClick={() => { setLaunchTemplateId(null); setModalOpen(true); }}
            style={{
              marginLeft: "auto",
              background: T.white, border: `1px solid ${T.border}`,
              color: T.dark, borderRadius: 9,
              padding: "8px 14px",
              fontWeight: T.fwBold, fontSize: T.fsSm5,
              cursor: "pointer",
            }}
          >
            + New playbook
          </button>
        )}
      </div>

      {/* ── Start a playbook ───────────────────────────────────────── */}
      <div style={{
        fontSize: T.fsXs, letterSpacing: "0.05em", textTransform: "uppercase",
        color: T.light, fontWeight: T.fwBold, margin: "22px 2px 11px",
      }}>
        Start a playbook
      </div>

      {templatesLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              background: T.card, border: `1px solid ${T.border}`,
              borderRadius: 13, height: 180,
              animation: "pulse 1.5s ease-in-out infinite",
            }} />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div style={{
          padding: "32px",
          border: `1px dashed ${T.border}`,
          borderRadius: 13,
          textAlign: "center",
          color: T.mid,
          fontSize: T.fsSm5,
        }}>
          No playbook templates yet. Create templates in Admin.
        </div>
      ) : (
        <div
          data-testid="template-tiles-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}
        >
          {templates.map((t) => (
            <TemplateTile
              key={t.id}
              template={t}
              canEdit={canEdit}
              onStart={handleStartTile}
            />
          ))}
        </div>
      )}

      {/* ── Running now ────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center",
        fontSize: T.fsXs, letterSpacing: "0.05em", textTransform: "uppercase",
        color: T.light, fontWeight: T.fwBold, margin: "22px 2px 11px",
        gap: 8,
      }}>
        <span>Running now</span>
        {!runsLoading && activeRuns.length > 0 && (
          <span style={{
            background: T.lightGray, borderRadius: 999,
            padding: "0 7px", color: T.mid,
            fontWeight: T.fwBold, fontSize: T.fsXs,
            textTransform: "none", letterSpacing: 0,
          }}>
            {activeRuns.length}
          </span>
        )}
        {!runsLoading && (
          <button
            type="button"
            data-testid="view-all-runs-link"
            onClick={() => navigate("/playbooks/runs")}
            style={{
              marginLeft: "auto",
              background: "transparent", border: "none",
              fontSize: T.fsXs5, fontWeight: T.fwBold,
              color: T.blue, cursor: "pointer",
              padding: 0, textTransform: "none", letterSpacing: 0,
            }}
          >
            View all runs →
          </button>
        )}
      </div>

      {runsLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <RunSkeleton />
          <RunSkeleton />
        </div>
      ) : activeRuns.length === 0 ? (
        <div
          data-testid="no-active-runs"
          style={{
            padding: "20px 16px",
            border: `1px solid ${T.border}`,
            borderRadius: 13,
            background: T.card,
            textAlign: "center",
            color: T.mid, fontSize: T.fsSm5,
          }}
        >
          No runs in progress. Start a playbook above to kick one off.
        </div>
      ) : (
        <div
          data-testid="active-runs-list"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          {activeRuns.map((run) => (
            <RunStrip
              key={run.id}
              run={run}
              onOpen={(id) => navigate(`/playbooks/runs/${encodeURIComponent(id)}`)}
            />
          ))}
        </div>
      )}

      {/* ── Launch modal ───────────────────────────────────────────── */}
      {modalOpen && (
        <PlaybookLaunchModal
          initialTemplateId={launchTemplateId}
          onClose={() => setModalOpen(false)}
          onLaunched={handleLaunched}
        />
      )}
    </div>
  );
}
